import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, Subject, tap } from 'rxjs';

import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { getActiveStreams } from 'app/utils/media/stream';

import { LeaveRoomType } from '../../social-leave-room/social-leave-room-type';
import { socialAppSelectors } from '../../state/app';
import { ArtifactService } from '../artifact/artifact.service';
import { ChatService } from '../chat.service';
import { CTAService } from '../cta/cta.service';
import { DeviceService } from '../device/device.service';
import { HostService } from '../host-service';
import { MonitoringService } from '../monitor/monitoring.service';
import { MultimediaUserService } from '../multimedia-user.service';
import { PhotoBoothService } from '../photo-booth.service';
import { PlaylistService } from '../playlist.service';
import { CrossRoomParticipantService } from '../presentation/cross-room-participant.service';
import { PresentationAudioService } from '../presentation/presentation-audio.service';
import { QAService } from '../qa.service';
import { RtcStatsStreamService } from '../rtc-stats-stream';
import { RtcStatsService } from '../rtc-stats.service';
import { SceneService } from '../scene.service';
import { ShareScreenParticipantService } from '../share-screen/share-screen-participant.service';
import { GroupShareScreenParticipantService } from '../share-screen-group/group-share-screen-participant.service';
import { GroupShareScreenService } from '../share-screen-group/group-share-screen.service';
import { SubscriptionService } from '../subscription/subscription.service';
import { ToasterVideoService } from '../toaster-video.service';
import { VideoEffectService } from '../video-effect.service';
import { VideoRoomService } from '../video-room.service';
import { VideoChatService } from '../videochat.service';
import { WebsocketTimerService } from '../websocket-timer.service';
import { WebsocketService } from '../websocket.service';

import { RoomPresenceService } from './room-presence.service';

@Injectable({
  providedIn: 'root',
})
export class RoomLifecycleService extends Loggable {
  private store = inject<Store<AppState>>(Store);
  private videoRoomService = inject(VideoRoomService);
  private websocketService = inject(WebsocketService);
  private multimediaUserService = inject(MultimediaUserService);
  private sceneService = inject(SceneService);
  private videoChatService = inject(VideoChatService);
  private deviceService = inject(DeviceService);
  private rtcStatsService = inject(RtcStatsService);
  private shareScreenParticipantService = inject(ShareScreenParticipantService);
  private photoBoothService = inject(PhotoBoothService);
  private websocketTimerService = inject(WebsocketTimerService);
  private subscriptionService = inject(SubscriptionService);
  private presentationAudioService = inject(PresentationAudioService);
  private rtcStatsStreamService = inject(RtcStatsStreamService);
  private qaService = inject(QAService);
  private videoEffectService = inject(VideoEffectService);
  private monitoringService = inject(MonitoringService);
  private toasterVideoService = inject(ToasterVideoService);
  private groupShareScreenService = inject(GroupShareScreenService);
  private groupShareParticipantService = inject(GroupShareScreenParticipantService);
  private hostService = inject(HostService);
  private ctaService = inject(CTAService);
  private artifactService = inject(ArtifactService);
  private chatService = inject(ChatService);
  private roomPresenceService = inject(RoomPresenceService);
  private crossRoomParticipantService = inject(CrossRoomParticipantService);
  private playlistService = inject(PlaylistService);

  public leaveRoomSubject = new Subject<LeaveRoomType>();
  public leaveRoom$ = this.leaveRoomSubject.asObservable();

  public constructor() {
    super();
    this.setDisplayName('RoomLifecycleService');

    this.store
      .select(socialAppSelectors.isInRoom)
      .pipe(
        filter((inRoom) => inRoom),
        tap(() => this.info(`Initializing room services`)),
      )
      .subscribe(() => this.initRoomServices());

    this.store
      .select(socialAppSelectors.isInRoom)
      .pipe(
        filter((inRoom) => !inRoom),
        tap(() => this.info(`Destroying room services`)),
      )
      .subscribe(() => this.destroyRoomServices());
  }

  private initRoomServices(): void {
    this.roomPresenceService.init();
    this.videoEffectService.init();
    this.videoRoomService.init();
    this.crossRoomParticipantService.init();
    this.websocketService.init();
    this.multimediaUserService.init();
    this.deviceService.init();
    this.subscriptionService.init();
    this.presentationAudioService.init();
    this.rtcStatsStreamService.init();
    this.toasterVideoService.init();
    this.groupShareScreenService.init();
    this.groupShareParticipantService.init();
    this.hostService.init();
    this.ctaService.init();
    this.artifactService.init();
    this.playlistService.init();
  }

  public destroyRoomServices(): void {
    try {
      this.roomPresenceService.destroy();
      this.hostService.destroy();
      void this.groupShareScreenService.destroy();
      this.videoRoomService.destroy();
      this.crossRoomParticipantService.destroy();
      this.multimediaUserService.destroy();
      void this.sceneService.destroy();
      this.videoChatService.destroy();
      void this.deviceService.destroy();
      this.rtcStatsService.destroy();
      void this.shareScreenParticipantService.destroy();
      this.photoBoothService.destroy();
      this.websocketTimerService.destroy();
      this.subscriptionService.destroy();
      this.presentationAudioService.destroy();
      this.rtcStatsStreamService.destroy();
      this.qaService.destroy();
      this.toasterVideoService.destroy();
      this.groupShareParticipantService.destroy();
      this.ctaService.destroy();
      this.artifactService.destroy();
      this.chatService.clear();
      void this.monitoringService.destroy();
      this.playlistService.destroy();
    } catch (e) {
      this.error('destroyRoomServices: Error destroying services', e);
    }

    // Avoid waiting on a round trip to get the user out of the room. We can continue on.
    void this.websocketService.close();
    this.websocketService.destroy();

    const activeStreams = getActiveStreams();
    if (Object.keys(activeStreams).length > 0) {
      this.warn('destroyRoomServices: Leaked media streams found', { streamIds: Object.keys(activeStreams) });
    }
  }
}
